import React from 'react';
import { Button, Card, CardBody, Input } from "reactstrap";
import { create, increase, cancel, BetCreatePayload } from "../../../helpers/api/bet";

import {
  BetLike,
  BetActionPayload,
  ParticipationUi,
} from "../Dashboard";
import {useAuthStore} from "../../../store/useAuthStore";

const QUICK_BET_AMOUNTS = [50, 100, 250, 500, 1000];

interface BetCardProps {
  participation: ParticipationUi;
  oppositeParticipation: ParticipationUi;
  bets: BetLike[];
  extraClass: string;
  onBetAction: (payload: BetActionPayload) => void;
  onRefreshBattleVolume: () => Promise<void>;
  onUpsertBet: (bet: BetLike) => void;
  onRemoveBet: (betId: string) => void;
  onReplaceUserBetForBattle: (bet: BetLike) => void;
  actionsDisabled?: boolean;
}

const BetCard: React.FC<BetCardProps> = ({
                                           participation,
                                           oppositeParticipation,
                                           bets,
                                           extraClass,
                                           onBetAction,
                                           onRefreshBattleVolume,
                                           onUpsertBet,
                                           onRemoveBet,
                                           onReplaceUserBetForBattle,
                                           actionsDisabled = false,
}) => {
  const debitPoints = useAuthStore((state) => state.debitPoints);
  const updatePoints = useAuthStore((state) => state.updatePoints);
  const currentUserId = useAuthStore((state) => state.user?.id);
  const currentUserBalance = useAuthStore((state) => state.user?.pointsBalance ?? 0);

  const [amountInput, setAmountInput] = React.useState('');
  const [amountNumber, setAmountNumber] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);

  const currentBet = React.useMemo(() => {
    return bets.find(
      (bet) =>
        bet.participationId === participation.id &&
        bet.status === 'placed' &&
        String(bet.userId) === String(currentUserId)
    ) ?? null;
  }, [bets, participation.id, currentUserId]);

  const oppositeBet = React.useMemo(() => {
    return bets.find(
      (bet) =>
        bet.participationId === oppositeParticipation.id &&
        bet.status === 'placed' &&
        String(bet.userId) === String(currentUserId)
    ) ?? null;
  }, [bets, oppositeParticipation.id, currentUserId]);

  const currentPool = participation.pool.totalVolume;
  const oppositePool = oppositeParticipation.pool.totalVolume;
  const currentStake = currentBet?.stakedPoints ?? 0;

  const previewStake = React.useMemo(() => {
    if (!amountNumber || amountNumber <= 0) {
      return currentStake;
    }

    return currentStake + amountNumber;
  }, [currentStake, amountNumber]);

  const previewPool = React.useMemo(() => {
    if (!amountNumber || amountNumber <= 0) {
      return currentPool;
    }

    return currentPool + amountNumber;
  }, [currentPool, amountNumber]);

  const previewShare = React.useMemo(() => {
    if (!previewPool || previewStake <= 0) return null;
    return (previewStake / previewPool) * 100;
  }, [previewStake, previewPool]);

  const previewProfit = React.useMemo(() => {
    if (!previewPool || previewStake <= 0) return null;
    return Math.round((previewStake / previewPool) * oppositePool);
  }, [previewStake, previewPool, oppositePool]);

  const previewPayout = React.useMemo(() => {
    if (previewProfit == null || previewStake <= 0) return null;
    return previewStake + previewProfit;
  }, [previewStake, previewProfit]);

  const parseAmount = (input: string): number | null => {
    if (!input?.trim()) return null;

    const normalized = input
      .replace(/\s|\u00A0/g, '')
      .replace(/[^\d]/g, '');

    if (!normalized) return null;

    const value = Number(normalized);

    if (Number.isNaN(value) || value <= 0) {
      return null;
    }

    return Math.round(value);
  };

  const onChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalizedValue = e.target.value.replace(/\D/g, '');
    setAmountInput(normalizedValue);
    setAmountNumber(parseAmount(normalizedValue));
  };

  const incrementQuickAmount = (amount: number) => {
    if (actionsDisabled || loading) return;

    const nextAmount = (amountNumber ?? 0) + amount;
    setAmountInput(String(nextAmount));
    setAmountNumber(nextAmount);
  };

  const handleCreateOrIncrease = async () => {
    if (actionsDisabled || !amountNumber || amountNumber <= 0 || loading) return;

    setLoading(true);

    try {
      if (currentBet) {
        const response = await increase(currentBet.id, amountNumber);

        onUpsertBet(response);
        debitPoints(amountNumber);
        await onRefreshBattleVolume();

        onBetAction({
          type: "increased",
          participationId: participation.id,
          amount: amountNumber,
          message: `Bet increased by ${amountNumber} PTS`,
        });
      } else {
        const payload: BetCreatePayload = {
          participationId: participation.id,
          stakedPoints: amountNumber,
        };

        const response = await create(payload);

        onReplaceUserBetForBattle(response);
        debitPoints(amountNumber);
        await onRefreshBattleVolume();

        onBetAction({
          type: "created",
          participationId: participation.id,
          amount: amountNumber,
          message: `Bet placed: ${amountNumber} PTS`,
        });
      }

      setAmountInput('');
      setAmountNumber(null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBet = async () => {
    if (actionsDisabled || !currentBet || loading) return;

    setLoading(true);

    try {
      await cancel(currentBet.id);

      onRemoveBet(currentBet.id);
      updatePoints(currentBet.stakedPoints);
      await onRefreshBattleVolume();

      onBetAction({
        type: "cancelled",
        participationId: participation.id,
        amount: currentBet.stakedPoints,
        message: `Bet cancelled: ${currentBet.stakedPoints} PTS refunded`,
      });

      setAmountInput('');
      setAmountNumber(null);

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isSwitchMode = !currentBet && !!oppositeBet;
  const mainButtonLabel = currentBet
    ? "Bet more"
    : isSwitchMode
      ? `Place bet`
      : "Place bet";

  return (
    <Card
      className="bet-card cyber-glass h-100"
      data-cy={`bet-card-${participation.id}`}
    >
      <CardBody>
        <div
          className={`bet-card-name ${extraClass}`}
          data-cy={`bet-card-name-${participation.id}`}
        >
          {participation.character.name}
        </div>

        <div className="d-flex align-items-end gap-2">
          <div
            className="bet-card-pool mb-0"
            data-cy={`bet-card-pool-${participation.id}`}
          >
            {currentPool}
          </div>
          <div className="text-muted">PTS</div>
        </div>

        <div className="text-muted">Current Pool</div>

        <div className="bet-stat-grid">
          <div className="bet-stat text-warning" data-cy={`bet-card-your-bet-${participation.id}`}>
            <div className="bet-stat-label">Your Bet</div>
            <div className="bet-stat-value">{currentStake > 0 ? `${currentStake} PTS` : '--- PTS'}</div>
          </div>

          <div className="bet-stat" data-cy={`bet-card-share-${participation.id}`}>
            <div className="bet-stat-label">Your Share</div>
            <div className="bet-stat-value">{previewShare != null ? `${previewShare.toFixed(2)} %` : '--- %'}</div>
          </div>

          <div className="bet-stat text-success" data-cy={`bet-card-profit-${participation.id}`}>
            <div className="bet-stat-label">Est. Profit</div>
            <div className="bet-stat-value">{previewProfit != null ? `${previewProfit} PTS` : '--- PTS'}</div>
          </div>

          <div className="bet-stat text-success" data-cy={`bet-card-payout-${participation.id}`}>
            <div className="bet-stat-label">Est. Payout</div>
            <div className="bet-stat-value">{previewPayout != null ? `${previewPayout} PTS` : '--- PTS'}</div>
          </div>
        </div>

        <div className="bet-card-actions d-flex gap-2">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={actionsDisabled ? "Betting is unavailable for this battle" : "Enter an amount..."}
            value={amountInput}
            onChange={onChangeAmount}
            disabled={actionsDisabled || loading}
            data-cy={`bet-card-input-${participation.id}`}
          />

          {!actionsDisabled && (
            <div className="bet-card-quick-amounts" data-cy={`bet-card-quick-amounts-${participation.id}`}>
              {QUICK_BET_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() => incrementQuickAmount(amount)}
                  disabled={loading || currentUserBalance <= 0}
                  data-cy={`bet-card-quick-${participation.id}-${amount}`}
                >
                  {amount}
                </Button>
              ))}
            </div>
          )}

          <Button
            color="primary"
            onClick={handleCreateOrIncrease}
            disabled={actionsDisabled || loading || !amountNumber}
            data-cy={`bet-card-submit-${participation.id}`}
          >
            {mainButtonLabel}
          </Button>

          <Button
            color="danger"
            onClick={handleCancelBet}
            disabled={actionsDisabled || loading || !currentBet}
            data-cy={`bet-card-cancel-${participation.id}`}
          >
            Cancel
          </Button>
        </div>
        {amountNumber && amountNumber > 0 && (
          <div className="text-info small mb-3" data-cy={`bet-card-preview-${participation.id}`}>
            Preview with +{amountNumber} PTS
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default BetCard;
